/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { css } from "@emotion/react";
import { FormProvider, useController, useForm } from "react-hook-form";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AdyenStore,
  AdyenStoreFormType,
  MutateAdyenStorePayload,
} from "../../models/adyen-business-line";
import {
  useCreateAdyenAccountHolderStore,
  useGetAdyenAccountHolderBusinessLines,
  useGetBalanceAccounts,
  useGetSplitConfigurations,
  usePushAdyenStoreToAdyen,
  useUpdateAdyenAccountHolderStore,
} from "../../service/adyen-account-holders-api";
import { getErrorMessage } from "../../../../error-handler";
import { TextInput } from "../../../../components/forms/inputs/TextInput";
import { colors } from "../../../../components/colors";
import {
  ButtonAction,
  ButtonInternalLink,
} from "../../../../components/NewButtons";
import { ActionSpinner } from "../../../../components/ActionSpinner";
import { SpinnerBlock } from "../../../common/Spinner";
import { TextAreaInput } from "../../../../components/forms/inputs/TextAreaInput";
import { SelectCountryInput } from "../../../../components/forms/inputs/SelectCountryInput";
import { SelectField } from "../../../../components/forms/fields/SelectField";
import { FormRow } from "../../../common/hook-form";
import { SelectInput } from "../../../../components/forms/inputs/SelectInput";

export const buttonWrapperStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  border-radius: 0px 0px 8px 8px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  width: 100%;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: end;
`;

export const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
`;

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  padding-bottom: 24px;
  width: 600px;
`;

export const bodySectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const bodyHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type Props = {
  store?: AdyenStore;
};

export const MutateStoreForm = ({ store }: Props) => {
  const { id, storeId } = useParams();
  const navigate = useNavigate();

  const schema = yup.object({
    description: yup.string().required("Description is required"),
    businessLineIds: yup
      .array()
      .of(yup.string())
      .min(1, "At least one business line is required"),
    defaultShopperStatement: yup
      .string()
      .max(20, "Default shopper statement must be at most 20 characters"),
    addressCountryCode: yup.string().required("Country code is required"),
    balanceAccountId: yup.string().when("splitConfigurationId", {
      is: (splitConfigurationId: string) => splitConfigurationId !== "",
      then: yup.string().required("Balance account is required"),
      otherwise: yup.string().optional(),
    }),
    splitConfigurationId: yup
      .string()
      .test(
        "splitConfigurationId",
        "Split configuration is required",
        (value, ctx) => {
          if (!ctx.parent.balanceAccountId) {
            return true;
          }
          return !!value;
        }
      ),
  });

  const methods = useForm<AdyenStoreFormType>({
    defaultValues: {
      description: store?.description || "",
      businessLineIds: store?.businessLines.map((bl) => bl.id) || [],
      addressCountryCode: store?.address.countryCode || "",
      addressAddressLine: store?.address.addressLine || "",
      addressAddressLine2: store?.address.addressLine2 || "",
      addressPostcode: store?.address.postcode || "",
      addressCity: store?.address.city || "",
      phoneNumber: store?.phoneNumber || "",
      defaultShopperStatement: store?.defaultShopperStatement || "",
      balanceAccountId: store?.splitConfiguration?.balanceAccountId || "",
      splitConfigurationId:
        store?.splitConfiguration?.splitConfigurationId || "",
    },
    resolver: yupResolver(schema),
  });

  const {
    data: businessLines,
    isLoading,
  } = useGetAdyenAccountHolderBusinessLines(id);

  const {
    data: splitConfigurations,
    isLoading: isLoadingSplitConfigurations,
  } = useGetSplitConfigurations();
  const {
    data: balanceAccounts,
    isLoading: isLoadingBalanceAccounts,
  } = useGetBalanceAccounts(id);

  const { mutateAsync: createStore } = useCreateAdyenAccountHolderStore();
  const { mutateAsync: updateStore } = useUpdateAdyenAccountHolderStore(
    storeId
  );
  const { mutateAsync: pushAdyenStoreToAdyen } = usePushAdyenStoreToAdyen(
    store?.id
  );

  const {
    field: { ref, value, onChange: controllerOnChange, ...controllerRest },
  } = useController({
    name: "businessLineIds",
    control: methods.control,
  });

  const onSubmit = async (data: AdyenStoreFormType) => {
    if (!id) {
      return;
    }

    let splitConfiguration: MutateAdyenStorePayload["splitConfiguration"] = null;

    if (data.splitConfigurationId && data.balanceAccountId) {
      splitConfiguration = {
        splitConfigurationId: data.splitConfigurationId,
        balanceAccountId: data.balanceAccountId,
      };
    }

    let storeId = store?.id;

    try {
      if (store) {
        await updateStore({
          data: {
            id: store.id,
            accountHolderId: id,
            description: data.description,
            businessLineIds: data.businessLineIds,
            phoneNumber: data.phoneNumber,
            defaultShopperStatement: data.defaultShopperStatement,
            address: {
              countryCode: data.addressCountryCode,
              addressLine: data.addressAddressLine,
              addressLine2: data.addressAddressLine2,
              postcode: data.addressPostcode,
              city: data.addressCity,
            },
            splitConfiguration,
          },
        });
      } else {
        const result = await createStore({
          data: {
            accountHolderId: id,
            description: data.description,
            businessLineIds: data.businessLineIds,
            phoneNumber: data.phoneNumber,
            defaultShopperStatement: data.defaultShopperStatement,
            address: {
              countryCode: data.addressCountryCode,
              addressLine: data.addressAddressLine,
              addressLine2: data.addressAddressLine2,
              postcode: data.addressPostcode,
              city: data.addressCity,
            },
            splitConfiguration,
          },
        });
        storeId = result.id;
      }
      await pushAdyenStoreToAdyen({
        data: { id: storeId! },
      });
      toastr.success(
        "Success",
        `Adyen store ${store ? "updated" : "created"} successfully`
      );
      navigate("..");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isLoading || isLoadingSplitConfigurations || isLoadingBalanceAccounts) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            <TextAreaInput
              name="description"
              label="Description"
              placeholder="Enter the description"
            />
            <SelectField
              isMulti
              closeMenuOnSelect={false}
              label="Business lines"
              placeholder="Start typing to search for business lines..."
              errorMessage={
                methods.getFieldState("businessLineIds", methods.formState)
                  .error?.message
              }
              onChange={(value) => {
                const values = value as { value: string; label: string }[];
                controllerOnChange(values.map((x) => x.value));
              }}
              options={
                businessLines?.map((bl) => ({
                  value: bl.id,
                  label: bl.industryCodeName,
                })) || []
              }
              value={(value || []).map((x) => ({
                value: x,
                label:
                  businessLines?.find((bl) => bl.id === x)?.industryCodeName ||
                  "",
              }))}
              innerRef={ref}
              {...controllerRest}
            />
            <SelectCountryInput
              name="addressCountryCode"
              isDisabled={!!store}
            />
            <TextInput
              name="phoneNumber"
              label="Phone number"
              helperText="Format: +45 12 34 56 78"
            />
            <TextInput
              name="defaultShopperStatement"
              label="Default shopper statement"
            />
            <TextInput name="addressAddressLine" label="Address line" />
            <TextInput name="addressAddressLine2" label="Address line 2" />
            <TextInput name="addressPostcode" label="Postcode" />
            <TextInput name="addressCity" label="City" />
            <FormRow>
              <SelectInput
                name="balanceAccountId"
                label="Split configuration balance account ID"
                options={[
                  {
                    value: "",
                    label: "None",
                  },
                  ...(balanceAccounts?.map((ba) => ({
                    value: ba.id,
                    label: ba.description || ba.reference,
                  })) || []),
                ]}
              />
              <SelectInput
                name="splitConfigurationId"
                label="Split configuration ID"
                options={[
                  {
                    value: "",
                    label: "None",
                  },
                  ...(splitConfigurations?.map((sc) => ({
                    value: sc.id,
                    label: sc.description || sc.reference,
                  })) || []),
                ]}
              />
            </FormRow>
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : store ? (
              "Update store"
            ) : (
              "Create store"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
